/**
 * The web app's personal-password rules (docs/members.md "Personal passwords"), kept free of the
 * DOM and of Preact so Node's test runner can check them (tests/passwords.test.ts). The words are
 * shared/passwords.ts's, which the native app shows too.
 */
import { PASSWORD_COPY, heldNameFor, newPasswordError } from "../shared/passwords.ts";
import { roleWord } from "./members-ui.ts";
import type { HousePerson, PeonList } from "../shared/types";

/**
 * The held name `typed` is (its registered spelling), or null when it is free or it is this
 * device's own person's name (signed in as them already, nothing to ask).
 */
export function heldNameTyped(typed: string, held: readonly string[] | undefined, ownName: string | null = null): string | null {
  const hit = heldNameFor((held ?? []).map((name) => ({ name })), typed);
  if (!hit) return null;
  if (ownName && heldNameFor([{ name: ownName }], hit.name)) return null;
  return hit.name;
}

/** The held names from GET /api/peons, [] from a server that does not send them. */
export function heldFrom(list: PeonList): string[] {
  return list.held ?? [];
}

/** What the account sheet's password form sends, or why it cannot yet (error null: incomplete, the button waits). */
export type PasswordSubmission = { ok: true; password: string; current?: string } | { ok: false; error: string | null };

/**
 * The account sheet's Password form. With no password yet: Password and Again. Changing one:
 * Current, New and Again, and `current` goes with it. Checked with the server's rule first.
 */
export function passwordSubmission(hasPassword: boolean, fields: { current: string; password: string; again: string }): PasswordSubmission {
  if ((hasPassword && !fields.current) || !fields.password || !fields.again) return { ok: false, error: null };
  const error = newPasswordError(fields.password, fields.again);
  if (error) return { ok: false, error };
  return hasPassword ? { ok: true, password: fields.password, current: fields.current } : { ok: true, password: fields.password };
}

/** "1 passkey", "2 passkeys", "No passkey": a person's passkeys in the House screen's people list. */
export function passkeyCountText(n: number): string {
  return n === 0 ? "No passkey" : `${n} passkey${n === 1 ? "" : "s"}`;
}

/** A person's line in the House screen: "Steward · 1 passkey · Password". */
export function personLine(p: Pick<HousePerson, "role" | "passkeys" | "password">): string {
  return [roleWord(p.role), passkeyCountText(p.passkeys.length), p.password ? PASSWORD_COPY.holds : PASSWORD_COPY.holdsNot].join(" · ");
}
